import { AlarmConfiguration } from 'domain/alarm-configuration'
import { useAuth } from 'layout/context/auth-context'
import { useLoading } from 'layout/context/loading-context'
import { useToast } from 'layout/context/toast-context'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { useEffect, useState } from 'react'
import { useAlarm } from '../hooks/useAlarm'
import { getConfigurationTypeFromCategory } from '../types/alarm-configuration-type'
import { AlarmCriterion } from '../types/alarm-criterion'
import { AhcrCriterionTypeEnum, AlarmCategory, AnprCriterionTypeEnum } from '../types/alarm.types'
import {
  alarmColors,
  blankAlarmCriterion,
  genderOptions,
} from '../types/alarm-consts'
import AlarmCriteriaForm from './alarm-criteria-form'
import AlarmCriteriaList from './alarm-criteria-list'

interface AlarmDialogProps {
  container: HTMLDivElement | null
  showDialog: boolean
  hideDialog: () => void
  onSuccess?: () => void
}

const AlarmDialog: React.FC<AlarmDialogProps> = ({
  showDialog,
  container,
  hideDialog,
  onSuccess,
}) => {
  const { user, clientId } = useAuth()
  const { isLoading } = useLoading()
  const { createAlarmConfiguration } = useAlarm()
  const { showWarn, showError, showSuccess } = useToast()
  const [criteria, setCriteria] = useState<AlarmCriterion[]>([])
  const [alarmCriterion, setAlarmCriterion] = useState<AlarmCriterion>(blankAlarmCriterion)
  const { ahcrAlarmCriteria, anprAlarmCriteria, fetchAlarmCriteria } = useAlarm()
  useEffect(() => {
    fetchAlarmCriteria()
  }, [])

  const resetForm = () => {
    setAlarmCriterion(blankAlarmCriterion)
    setCriteria([])
  }

  const handleCategoryChange = (newCategory: AlarmCategory) => {
    setAlarmCriterion((prev) => ({
      ...prev,
      category: newCategory,
      type: newCategory === 'ANPR' ? 'LICENSE_PLATE' : 'GENDER',
    }))
  }

  const addCriterion = () => {
    if (!alarmCriterion.value.trim()) {
      showWarn('Atenção', 'Informe um valor antes de adicionar')
      return
    }

    // Check if there's already a criterion of this TYPE (regardless of value)
    const typeAlreadyExists = criteria.some(
      (c) => c.type === alarmCriterion.type
    )

    if (typeAlreadyExists) {
      showError(
        'Tipo Duplicado',
        'Não é possível adicionar múltiplos critérios do mesmo tipo. Remova o existente primeiro.',
        4000
      )
      return
    }

    let displayValue = alarmCriterion.displayValue

    if (alarmCriterion.type === AnprCriterionTypeEnum.COLOR) {
      displayValue =
        alarmColors.find((c) => c.hex === alarmCriterion.value)?.name ||
        alarmCriterion.value
    } else if (
      alarmCriterion.type === AhcrCriterionTypeEnum.JACKET_COLOR ||
      alarmCriterion.type === AhcrCriterionTypeEnum.TROUSER_COLOR
    ) {
      displayValue =
        alarmColors.find((c) => c.hex === alarmCriterion.value)?.name ||
        alarmCriterion.value
    } else if (alarmCriterion.type === AhcrCriterionTypeEnum.GENDER) {
      displayValue =
        genderOptions.find((g) => g.value === alarmCriterion.value)?.label ||
        alarmCriterion.value
    }

    const newCriterion: AlarmCriterion = {
      id: `${alarmCriterion.category}-${alarmCriterion.type}-${Date.now()}`,
      category: alarmCriterion.category,
      type: alarmCriterion.type,
      value: alarmCriterion.value,
      displayValue,
    }

    setCriteria([...criteria, newCriterion])

    setAlarmCriterion((prev) => ({ ...prev, value: '' }))

    showSuccess('Adicionado', 'Critério adicionado com sucesso', 2000)
  }

  const removeCriterion = (id: string) => {
    setCriteria(criteria.filter((c) => c.id !== id))
  }

  const saveConfiguration = async () => {
    if (criteria.length === 0) {
      showError('Erro', 'Adicione pelo menos um critério')
      return
    }

    const configurationType = getConfigurationTypeFromCategory(
      criteria[0].category
    )

    // Map criteria to AlarmCriteria format (just type and value)
    const mappedCriteria = criteria.map((criterion) => ({
      type: criterion.type,
      value: criterion.value,
    }))

    const configData: AlarmConfiguration = {
      type: configurationType,
      createdDate: new Date(),
      clientId: clientId ?? '',
      createdBy: user?.name ?? 'SYSTEM',
      id: '',
      criterias: mappedCriteria,
    }

    const success = await createAlarmConfiguration(clientId, configData)

    if (success) {
      hideDialog()
      resetForm()
      setCriteria([])
      // Trigger refresh of the configurations list
      onSuccess?.()
    }
  }

  const configurationDialogFooter = (
    <div className='alarms-dialog-footer'>
      <Button
        label='Cancelar'
        icon='pi pi-times'
        outlined
        className='alarms-btn-cancel'
        onClick={() => {
          hideDialog()
          resetForm()
        }}
      />
      <Button
        label='Salvar'
        icon='pi pi-check'
        className='alarms-btn-save'
        onClick={saveConfiguration}
        loading={isLoading}
        disabled={criteria.length === 0}
      />
    </div>
  )

  return (
    <>
      <Dialog
        appendTo={container}
        visible={showDialog}
        style={{ width: '1200px', maxHeight: '90vh' }}
        header={
          <div className='alarms-dialog-header'>
            <i className='pi pi-cog alarms-dialog-icon' />
            <span>Nova Configuração de Alarme</span>
          </div>
        }
        modal
        className='alarms-dialog'
        footer={configurationDialogFooter}
        onHide={() => {
          hideDialog()
          resetForm()
        }}
      >
        <div className='alarms-form alarms-form-split'>
          <AlarmCriteriaForm
            alarmCriterion={alarmCriterion}
            anprAlarmCriteria={anprAlarmCriteria}
            ahcrAlarmCriteria={ahcrAlarmCriteria}
            criteria={criteria}
            onCriterionChange={setAlarmCriterion}
            onCategoryChange={handleCategoryChange}
            onAddCriterion={addCriterion}
          />
          <AlarmCriteriaList
            criteria={criteria}
            onRemoveCriterion={removeCriterion}
          />
        </div>
      </Dialog>
    </>
  )
}

export default AlarmDialog
